import * as THREE from 'three';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import { getAircraftType } from './AircraftCatalog.js';

function prismGeometry(points, thickness) {
  const vertices = [];
  const indices = [];
  const half = thickness / 2;
  for (const y of [-half, half]) {
    for (const [x, z] of points) vertices.push(x, y, z);
  }
  const count = points.length;
  for (let index = 1; index < count - 1; index += 1) {
    indices.push(0, index + 1, index);
    indices.push(count, count + index, count + index + 1);
  }
  for (let index = 0; index < count; index += 1) {
    const next = (index + 1) % count;
    indices.push(index, next, count + next, index, count + next, count + index);
  }
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(vertices, 3));
  geometry.setIndex(indices);
  geometry.computeVertexNormals();
  return geometry;
}

function shadowed(mesh) {
  mesh.castShadow = true;
  mesh.receiveShadow = true;
  return mesh;
}

function mergeMeshesByMaterial(group, excluded = new Set()) {
  const buckets = new Map();
  for (const child of [...group.children]) {
    if (!child.isMesh || excluded.has(child) || Array.isArray(child.material)) continue;
    if (!buckets.has(child.material)) buckets.set(child.material, []);
    buckets.get(child.material).push(child);
  }

  for (const [material, meshes] of buckets) {
    if (meshes.length < 2) continue;
    const geometries = meshes.map((mesh) => {
      mesh.updateMatrix();
      const clone = mesh.geometry.clone();
      const geometry = clone.index ? clone.toNonIndexed() : clone;
      if (geometry !== clone) clone.dispose();
      geometry.deleteAttribute('uv');
      geometry.applyMatrix4(mesh.matrix);
      return geometry;
    });
    const geometry = mergeGeometries(geometries, false);
    geometries.forEach((item) => item.dispose());
    if (!geometry) continue;
    const merged = new THREE.Mesh(geometry, material);
    merged.castShadow = meshes.some((mesh) => mesh.castShadow);
    merged.receiveShadow = meshes.some((mesh) => mesh.receiveShadow);
    meshes.forEach((mesh) => {
      group.remove(mesh);
      mesh.geometry.dispose();
    });
    group.add(merged);
  }
}

export class AircraftView {
  constructor(scene, {
    team = 'blue', typeId = 'falcon', isPlayer = false, lowPower = false,
    castShadows = true
  } = {}) {
    this.scene = scene;
    this.team = team;
    this.type = getAircraftType(typeId);
    this.isPlayer = isPlayer;
    this.lowPower = lowPower;
    this.castShadows = castShadows;
    this.group = new THREE.Group();
    this.group.name = `${team}-${this.type.id}-aircraft`;
    this.group.rotation.order = 'YXZ';
    this.materials = [];
    this.createModel();
    if (!this.castShadows) {
      this.group.traverse((child) => {
        if (child.isMesh) child.castShadow = false;
      });
    }
    scene.add(this.group);
  }

  createModel() {
    const simplified = this.lowPower && !this.isPlayer;
    const teamColor = this.team === 'blue' ? '#1677ff' : '#f2394f';
    const teamDark = this.team === 'blue' ? '#07295f' : '#5b0918';
    const pearl = this.lowPower
      ? new THREE.MeshStandardMaterial({
        color: this.team === 'blue' ? '#cedeff' : '#ffd2d7', metalness: 0.58, roughness: 0.28
      })
      : new THREE.MeshPhysicalMaterial({
        color: this.team === 'blue' ? '#cedeff' : '#ffd2d7', metalness: 0.64,
        roughness: 0.23, clearcoat: 1, clearcoatRoughness: 0.12
      });
    const navy = this.lowPower
      ? new THREE.MeshStandardMaterial({ color: teamDark, metalness: 0.62, roughness: 0.25 })
      : new THREE.MeshPhysicalMaterial({
        color: teamDark, metalness: 0.7, roughness: 0.2, clearcoat: 0.9
      });
    const accent = new THREE.MeshStandardMaterial({
      color: teamColor, emissive: teamColor, emissiveIntensity: 0.34,
      metalness: 0.68, roughness: 0.22
    });
    const dark = new THREE.MeshStandardMaterial({
      color: '#10151d', metalness: 0.7, roughness: 0.3
    });
    const glass = this.lowPower
      ? new THREE.MeshStandardMaterial({
        color: this.team === 'blue' ? '#0b253d' : '#351526', metalness: 0.2,
        roughness: 0.12, transparent: true, opacity: 0.9
      })
      : new THREE.MeshPhysicalMaterial({
        color: this.team === 'blue' ? '#0b253d' : '#351526', metalness: 0.12,
        roughness: 0.06, transmission: 0.35, transparent: true, opacity: 0.88, clearcoat: 1
      });

    this.materials.push(pearl, navy, accent, dark, glass);

    const profile = [
      new THREE.Vector2(0.04, -6.8),
      new THREE.Vector2(0.46, -6.25),
      new THREE.Vector2(0.92, -4.5),
      new THREE.Vector2(1.05, -0.4),
      new THREE.Vector2(0.82, 4.3),
      new THREE.Vector2(0.46, 6.2),
      new THREE.Vector2(0.28, 6.8)
    ];
    const fuselage = shadowed(new THREE.Mesh(
      new THREE.LatheGeometry(profile, this.lowPower ? 16 : 26), pearl
    ));
    fuselage.rotation.x = Math.PI / 2;
    this.group.add(fuselage);

    const wingPoints = [
      [-7.7, -0.6], [-1.1, -3.5], [1.1, -3.5], [7.7, -0.6],
      [6.4, 1.65], [1.15, 1.25], [-1.15, 1.25], [-6.4, 1.65]
    ];
    const wings = shadowed(new THREE.Mesh(prismGeometry(wingPoints, 0.3), navy));
    wings.position.set(0, 0.05, -0.2);
    this.group.add(wings);

    const stripePoints = [
      [-7.1, -0.25], [-1.8, -2.7], [1.8, -2.7], [7.1, -0.25],
      [6.8, 0.35], [1.5, -1.85], [-1.5, -1.85], [-6.8, 0.35]
    ];
    const wingStripe = new THREE.Mesh(prismGeometry(stripePoints, 0.045), accent);
    wingStripe.position.y = 0.22;
    this.group.add(wingStripe);

    const tail = shadowed(new THREE.Mesh(
      prismGeometry([[-3.6, -0.2], [-0.4, -1.3], [0.4, -1.3], [3.6, -0.2], [3.1, 1], [-3.1, 1]], 0.2),
      navy
    ));
    tail.position.set(0, 0.1, 5.1);
    this.group.add(tail);

    const finShape = new THREE.Shape();
    finShape.moveTo(0, 0);
    finShape.lineTo(0, 2.9);
    finShape.lineTo(0.44, 3.25);
    finShape.lineTo(1.3, 0);
    finShape.closePath();
    const fin = shadowed(new THREE.Mesh(
      new THREE.ExtrudeGeometry(finShape, { depth: 0.18, bevelEnabled: true, bevelSize: 0.06, bevelThickness: 0.05 }),
      navy
    ));
    fin.rotation.y = Math.PI / 2;
    fin.position.set(-0.09, 0.45, 4.55);
    this.group.add(fin);

    const cockpit = shadowed(new THREE.Mesh(
      new THREE.SphereGeometry(1, this.lowPower ? 16 : 24, this.lowPower ? 8 : 12), glass
    ));
    cockpit.scale.set(0.78, 0.53, 1.82);
    cockpit.position.set(0, 0.8, -2.05);
    this.group.add(cockpit);

    const nose = shadowed(new THREE.Mesh(new THREE.ConeGeometry(0.48, 2.2, 24), pearl));
    nose.rotation.x = -Math.PI / 2;
    nose.position.z = -7.2;
    this.group.add(nose);

    if (!simplified) {
      const intakeGeometry = new THREE.CylinderGeometry(0.48, 0.62, 1.55, 18);
      for (const side of [-1, 1]) {
        const intake = shadowed(new THREE.Mesh(intakeGeometry, dark));
        intake.rotation.x = Math.PI / 2;
        intake.position.set(side * 1.12, -0.1, 2.9);
        this.group.add(intake);
        const nozzle = new THREE.Mesh(
          new THREE.CircleGeometry(0.38, 18),
          new THREE.MeshBasicMaterial({ color: teamColor, transparent: true, opacity: 0.82 })
        );
        nozzle.rotation.x = Math.PI;
        nozzle.position.set(side * 1.12, -0.1, 3.72);
        this.group.add(nozzle);
      }
    }

    const afterburnerMaterial = new THREE.MeshBasicMaterial({
      color: teamColor, transparent: true, opacity: 0.7,
      blending: THREE.AdditiveBlending, depthWrite: false
    });
    this.afterburner = new THREE.Mesh(
      new THREE.SphereGeometry(0.62, this.lowPower ? 8 : 12, this.lowPower ? 5 : 7),
      afterburnerMaterial
    );
    this.afterburner.scale.set(1.1, 0.72, 2.4);
    this.afterburner.position.set(0, -0.1, 4.3);
    this.group.add(this.afterburner);

    if (!simplified) {
      const gearMaterial = new THREE.MeshStandardMaterial({ color: '#202427', roughness: 0.7 });
      const wheelGeometry = new THREE.TorusGeometry(0.34, 0.14, 8, 18);
      this.gear = new THREE.Group();
      for (const [x, z] of [[-1.75, -0.6], [1.75, -0.6], [0, 4.25]]) {
        const strut = shadowed(new THREE.Mesh(new THREE.CylinderGeometry(0.055, 0.08, 1.45, 8), dark));
        strut.position.set(x, -0.78, z);
        strut.rotation.z = x === 0 ? 0 : x > 0 ? -0.22 : 0.22;
        const wheel = shadowed(new THREE.Mesh(wheelGeometry, gearMaterial));
        wheel.position.set(x, -1.52, z);
        wheel.rotation.y = Math.PI / 2;
        this.gear.add(strut, wheel);
      }
      this.group.add(this.gear);

      const red = new THREE.MeshBasicMaterial({ color: '#ff2f36' });
      const green = new THREE.MeshBasicMaterial({ color: '#43ffa0' });
      const lightGeometry = new THREE.SphereGeometry(0.18, 12, 8);
      const leftLight = new THREE.Mesh(lightGeometry, red);
      leftLight.position.set(-7.65, 0.18, -0.45);
      const rightLight = new THREE.Mesh(lightGeometry, green);
      rightLight.position.set(7.65, 0.18, -0.45);
      this.group.add(leftLight, rightLight);
    }

    if (this.type.id === 'viper') {
      wings.scale.z = 0.82;
      tail.scale.x = 0.84;
    } else if (this.type.id === 'titan') {
      wings.scale.x = 1.14;
      fuselage.scale.set(1.16, 1.16, 1.05);
    }
    if (this.gear) mergeMeshesByMaterial(this.gear);
    mergeMeshesByMaterial(this.group, new Set([this.afterburner]));
    this.group.scale.setScalar(this.type.scale);
  }

  update(flight, _dt) {
    this.group.position.set(flight.position.x, flight.position.y, flight.position.z);
    this.group.rotation.set(flight.pitch, flight.heading, flight.roll);
    const power = flight.throttle ?? 0.7;
    this.afterburner.material.opacity = 0.38 + power * 0.5;
    this.afterburner.scale.z = 1.3 + power * 2.2;
    if (this.gear) {
      const gearDown = flight.grounded || flight.gearDown;
      this.gear.position.y = THREE.MathUtils.lerp(this.gear.position.y, gearDown ? 0 : 0.5, 0.07);
      this.gear.scale.setScalar(THREE.MathUtils.lerp(this.gear.scale.x, gearDown ? 1 : 0.22, 0.08));
    }
  }

  setVisible(visible) {
    this.group.visible = visible;
  }

  dispose() {
    this.scene.remove(this.group);
    const materials = new Set();
    this.group.traverse((child) => {
      child.geometry?.dispose?.();
      if (Array.isArray(child.material)) child.material.forEach((material) => materials.add(material));
      else if (child.material) materials.add(child.material);
    });
    materials.forEach((material) => material.dispose());
  }
}
